import { SentryError } from "../sentry/SentryError";
import { amfamIntegrationErrorDetails } from "./errorTypes/amfam-integration";
import { ensiteIntegrationErrorDetails } from "./errorTypes/ensite-integration";
import { homeownerPortalErrorDetails } from "./errorTypes/homeowner-portal";
import { inspectifyIntegrationErrorDetails } from "./errorTypes/inspectify-integration";
import { jobSchedulerErrorDetails } from "./errorTypes/job-scheduler";
import { mobileApiErrorDetails } from "./errorTypes/mobile-api";
import { oddjobApiErrorDetails } from "./errorTypes/oddjob-api";
import { pdfRendererErrorDetails } from "./errorTypes/pdf-renderer";
import { rdclIntegrationErrorDetails } from "./errorTypes/rdcl-integration";
import { type ErrorDetails, RemiApp, type RemiHttpStatusCode, remiHttpCodeToNumber } from "./errorTypes/remi-app";
import { remiNextjsErrorDetails } from "./errorTypes/remi-nextjs";
import { sunrunAutomationsErrorDetails } from "./errorTypes/sunrun-automations";
import { sunrunIntegrationErrorDetails } from "./errorTypes/sunrun-integration";
import { trpcRouterErrorDetails } from "./errorTypes/trpc-router";
import { webhookProcessorErrorDetails } from "./errorTypes/webhook-processor";
import { westhillIntegrationErrorDetails } from "./errorTypes/westhill-integration";
import type { RemiAppErrorInput } from "./remiAppErrorInput";

export class RemiAppError extends SentryError<RemiApp, RemiAppErrorInput["errorType"], RemiAppErrorInput["data"]> {
  private readonly _httpCode: RemiHttpStatusCode;
  constructor(input: RemiAppErrorInput) {
    const { remiHttpCode, sentryDescription } = getErrorDetails(input);
    const customFingerprintPrefix = input.fingerprintPrefix ? `${input.fingerprintPrefix} · ` : "";
    const name = `${customFingerprintPrefix}${input.app} · ${input.errorType}`;
    const message = sentryDescription;
    super({
      name,
      message,
      severity: input.severity,
      service: input.app,
      errorType: input.errorType,
      data: input.data,
      publicMessage: sentryDescription,
      customFingerprintPrefix: input.fingerprintPrefix,
      metric: input.metric,
    });

    this._httpCode = remiHttpCodeToNumber[remiHttpCode];
  }

  getSentryData() {
    const tags: Record<string, string | boolean | number> = {
      app: this.service,
      error_type: this.errorType,
      severity: this.severity,
      http_code: this._httpCode,
    };
    if (this.token) {
      tags.token = this.token;
    }
    if (this.origin) {
      tags.origin = this.origin;
    }
    return {
      tags,
      attributes: {
        data: this.reportedData,
      },
      fingerprint: [this.service, this.errorType, this.severity],
    };
  }

  get httpCode(): RemiHttpStatusCode {
    return this._httpCode;
  }
}

function getErrorDetails(input: RemiAppErrorInput): ErrorDetails {
  switch (input.app) {
    case RemiApp.JOB_SCHEDULER:
      return jobSchedulerErrorDetails[input.errorType];
    case RemiApp.WEBHOOK_PROCESSOR:
      return webhookProcessorErrorDetails[input.errorType];
    case RemiApp.TRPC_ROUTER:
      return trpcRouterErrorDetails[input.errorType];
    case RemiApp.SUNRUN_AUTOMATIONS:
      return sunrunAutomationsErrorDetails[input.errorType];
    case RemiApp.REMI_NEXTJS:
      return remiNextjsErrorDetails[input.errorType];
    case RemiApp.HOMEOWNER_PORTAL:
      return homeownerPortalErrorDetails[input.errorType];
    case RemiApp.WESTHILL_INTEGRATION:
      return westhillIntegrationErrorDetails[input.errorType];
    case RemiApp.RDCL_INTEGRATION:
      return rdclIntegrationErrorDetails[input.errorType];
    case RemiApp.AMFAM_INTEGRATION:
      return amfamIntegrationErrorDetails[input.errorType];
    case RemiApp.ENSITE_INTEGRATION:
      return ensiteIntegrationErrorDetails[input.errorType];
    case RemiApp.INSPECTIFY_INTEGRATION:
      return inspectifyIntegrationErrorDetails[input.errorType];
    case RemiApp.SUNRUN_INTEGRATION:
      return sunrunIntegrationErrorDetails[input.errorType];
    case RemiApp.ODDJOB_API:
      return oddjobApiErrorDetails[input.errorType];
    case RemiApp.MOBILE_API:
      return mobileApiErrorDetails[input.errorType];
    case RemiApp.PDF_RENDERER:
      return pdfRendererErrorDetails[input.errorType];
    // Do not add a default case to this switch statement so that lint fails if the list is not exhaustive
  }
}
